/**
 * TanStack Query hooks for Admin Tracking Management API
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/api-client";
import type {
    AdminTrackingFilters,
    CompanyContainersResponse,
    CompanyTrackingListResponse,
    CompanyUsageBreakdown,
    SyncEstimate,
    SyncResult,
    TrackingUsageStats,
} from "@/types/api/admin-tracking";

// ============================================================================
// Query Keys
// ============================================================================

export const ADMIN_TRACKING_KEYS = {
    all: ["admin-tracking"] as const,
    companies: (filters?: AdminTrackingFilters) =>
        [...ADMIN_TRACKING_KEYS.all, "companies", filters] as const,
    companyContainers: (companyId: string) =>
        [...ADMIN_TRACKING_KEYS.all, "company-containers", companyId] as const,
    estimate: (params: { companyId?: string; containerId?: string }) =>
        [...ADMIN_TRACKING_KEYS.all, "estimate", params] as const,
    usage: () => [...ADMIN_TRACKING_KEYS.all, "usage"] as const,
    usageBreakdown: () => [...ADMIN_TRACKING_KEYS.all, "usage-breakdown"] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch companies with active containers and tracking stats.
 */
export function useTrackingCompanies(filters?: AdminTrackingFilters) {
    return useQuery({
        queryKey: ADMIN_TRACKING_KEYS.companies(filters),
        queryFn: async (): Promise<CompanyTrackingListResponse> => {
            return await apiGet<CompanyTrackingListResponse>("/admin/tracking/companies", {
                params: {
                    ...(filters?.company_type ? { company_type: filters.company_type } : {}),
                    ...(filters?.search ? { search: filters.search } : {}),
                },
            });
        },
        staleTime: 30_000,
    });
}

/**
 * Fetch containers for a company with tracking eligibility.
 */
export function useTrackingCompanyContainers(companyId: string, enabled = true) {
    return useQuery({
        queryKey: ADMIN_TRACKING_KEYS.companyContainers(companyId),
        queryFn: async (): Promise<CompanyContainersResponse> => {
            return await apiGet<CompanyContainersResponse>(
                `/admin/tracking/companies/${companyId}/containers`
            );
        },
        enabled: !!companyId && enabled,
        staleTime: 15_000,
    });
}

/**
 * Estimate API calls and cost before sync (dry run).
 */
export function useSyncEstimate(params: { companyId?: string; containerId?: string }) {
    return useQuery({
        queryKey: ADMIN_TRACKING_KEYS.estimate(params),
        queryFn: async (): Promise<SyncEstimate> => {
            return await apiGet<SyncEstimate>("/admin/tracking/estimate", {
                params: {
                    ...(params.companyId ? { company_id: params.companyId } : {}),
                    ...(params.containerId ? { container_id: params.containerId } : {}),
                },
            });
        },
        enabled: !!(params.companyId || params.containerId),
        staleTime: 10_000,
    });
}

/**
 * Fetch global JSONCargo API usage stats.
 */
export function useTrackingUsageStats() {
    return useQuery({
        queryKey: ADMIN_TRACKING_KEYS.usage(),
        queryFn: async (): Promise<TrackingUsageStats> => {
            return await apiGet<TrackingUsageStats>("/admin/tracking/usage");
        },
        staleTime: 60_000,
    });
}

/**
 * Fetch per-company API usage breakdown.
 */
export function useTrackingUsageBreakdown() {
    return useQuery({
        queryKey: ADMIN_TRACKING_KEYS.usageBreakdown(),
        queryFn: async (): Promise<CompanyUsageBreakdown[]> => {
            return await apiGet<CompanyUsageBreakdown[]>("/admin/tracking/usage/breakdown");
        },
        staleTime: 60_000,
    });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Sync all trackable containers for a company.
 */
export function useSyncCompany() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (companyId: string): Promise<SyncResult> => {
            return await apiPost<SyncResult>(`/admin/tracking/sync/company/${companyId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ADMIN_TRACKING_KEYS.all });
        },
    });
}

/**
 * Sync a single container.
 */
export function useSyncContainer() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (containerId: string): Promise<SyncResult> => {
            return await apiPost<SyncResult>(`/admin/tracking/sync/container/${containerId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ADMIN_TRACKING_KEYS.all });
        },
    });
}
